import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  Output,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { TranslocoDatePipe } from '@jsverse/transloco-locale';
import { FroalaViewModule } from 'angular-froala-wysiwyg';
import moment from 'moment';
import { getOrganisationImage, GuidelineService, GuidelineViewService, InsightsService, TrackingEvent } from 'shared/domain';
import { RichTextViewComponent } from 'shared/feature-rich-text-view';
import { ButtonDirective, IconDirective, KeyValueComponent, PopoverService, TooltipDirective } from 'shared/ui-common';
import {
  AddZToUTCPipe,
  IsConnectedGuidelinePipe,
  isConnectedGuideline,
  isDateInPast,
  isDateWithinUCUM,
  NamePipe,
  UtcToLocalPipe,
  WrapFnPipe,
  transformNameToDisplayName,
} from 'shared/util-common';

import { CommentableElementDirective, CommentableTextDirective } from '@treetop/shared/comments';
import { GuidelineReviewStatus, GuidelineOrigin, GuidelineBase, User, Ucum, ResourceTag } from '@treetop/shared/model';
import { AddResourceTagsComponent, ResourceTagListComponent } from '@treetop/shared/tt-resource-tags';

@Component({
  selector: 'tt-smartbook-header',
  standalone: true,
  templateUrl: './smartbook-header.component.html',
  styleUrls: ['./smartbook-header.component.scss'],
  imports: [
    TranslocoPipe,
    FroalaViewModule,
    TooltipDirective,
    KeyValueComponent,
    MatIconModule,
    IconDirective,
    WrapFnPipe,
    IsConnectedGuidelinePipe,
    CommentableElementDirective,
    RichTextViewComponent,
    CommentableTextDirective,
    NamePipe,
    UtcToLocalPipe,
    TranslocoDatePipe,
    AddZToUTCPipe,
    ResourceTagListComponent,
    AddResourceTagsComponent,
    ButtonDirective,
  ],
})
export class SmartbookHeaderComponent {
  protected router = inject(Router);
  private insightsService = inject(InsightsService);
  private popoverService = inject(PopoverService);
  private viewContainerRef = inject(ViewContainerRef);
  protected guidelineService = inject(GuidelineService);
  protected guidelineViewService = inject(GuidelineViewService, { optional: true });

  public guidelineData = input<GuidelineBase>();
  public isRevisionDateEnabled = input<boolean>(false);
  public soonToExpirePeriod = input<Ucum>();
  public showContentChangedBanner = input<boolean>(false);
  public showContentEditingBanner = input<boolean>(false);

  private tagTriggerButton = viewChild<ElementRef>('tagTriggerButton');
  private addResourceTagsTemplate = viewChild<TemplateRef<unknown>>('addResourceTagsTemplate');

  private isExpired = computed(() => {
    return this.isRevisionDateEnabled() && this.guidelineData()?.revisionDate ? isDateInPast(this.guidelineData().revisionDate) : false;
  });

  private isExpiringSoon = computed(() => {
    return this.isRevisionDateEnabled() && this.guidelineData()?.revisionDate
      ? isDateWithinUCUM(this.guidelineData().revisionDate, this.soonToExpirePeriod())
      : false;
  });

  private editString = computed(() => {
    return this.generateDateString(this.guidelineData().updatedAt as Date, this.guidelineData().editor);
  });

  private reviewerString = computed(() => {
    const guidelineData = this.guidelineData();
    if (!guidelineData) {
      return '';
    }

    if (!guidelineData.reviews || !guidelineData.reviews.length) {
      return '';
    }

    return guidelineData.reviews
      .filter((review) => review.status === GuidelineReviewStatus.Approved)
      .map((review) => `${review.reviewer.firstName} ${review.reviewer.lastName}`)
      .join(', ');
  });

  private approvedString = computed(() => {
    return this.generateDateString(this.guidelineData().approvedAt, this.guidelineData().approvedBy);
  });

  private publishedString = computed(() => {
    return this.generateDateString(this.guidelineData().publishedAt, this.guidelineData().publisher);
  });

  protected vm = computed(() => {
    const guidelineData = this.guidelineData();

    return {
      editString: this.editString(),
      reviewerString: this.reviewerString(),
      approvedString: this.approvedString(),
      publishedString: this.publishedString(),
      isAutomaticUpdateEnabled: guidelineData?.connectedGuidelineInformation?.automaticUpdates,
      isExpired: this.isExpired(),
      isExpiringSoon: this.isExpiringSoon(),
      additionalInfo: guidelineData?.additionalInfo,
      uniqueId: guidelineData?.uniqueId,
      owner: guidelineData?.owner,
      authors: guidelineData?.authors,
      revisionDate: guidelineData?.revisionDate,
      title: guidelineData?.title,
      organisation: guidelineData?.organisation,
      isPublishedWithTags: guidelineData?.isPublishedWithTags,
      updateMessage: guidelineData?.updateMessage,
      version: guidelineData?.version,
      guidelineOrigin: guidelineData?.guidelineOrigin,
      originalGuidelineInfo: {
        organization: guidelineData?.providerOrganisation,
        originalVersion: guidelineData?.connectedGuidelineInformation?.originalGuidelineInformation?.version,
        owner: guidelineData?.connectedGuidelineInformation?.originalGuidelineInformation?.owner,
        authors: guidelineData?.connectedGuidelineInformation?.originalGuidelineInformation?.authors,
        publishDate: guidelineData?.connectedGuidelineInformation?.originalGuidelineInformation?.publishedAt,
      },
    };
  });

  @Input() public isPublicPage?: boolean;

  @Input() public isPreviewMode: boolean;
  @Input() public organisationImage: string;

  @Output() public triggerUpdateDialog = new EventEmitter<void>();

  protected openTagDropdown(event: MouseEvent): void {
    event.stopPropagation();

    const triggerEl = this.tagTriggerButton();
    const template = this.addResourceTagsTemplate();
    if (!triggerEl || !template) return;

    this.popoverService.open(template, triggerEl.nativeElement, { panelClass: 'tt-tag-dropdown-panel' }, this.viewContainerRef);
  }

  protected removeTag(tag: ResourceTag): void {
    const updatedTags = (this.guidelineData()?.resourceTags ?? []).filter((t) => t.id !== tag.id);
    this.handleTagsChanged(updatedTags);
  }

  protected handleTagsChanged(tags: ResourceTag[]): void {
    if (isConnectedGuideline(this.guidelineData()?.guidelineOrigin)) {
      this.guidelineService.updateGuidelineHeaderLocalChanges({ resourceTags: tags });
    } else {
      this.guidelineService.updateGuidelineHeader({ resourceTags: tags });
    }
  }

  protected navigateBack() {
    this.router.navigateByUrl('/smartbook');
  }

  protected onWhatsNewClick(): void {
    this.insightsService.trackEvent(TrackingEvent.WHATS_NEW_CLICKED, {
      guidelineTitle: this.guidelineData()?.title,
    });
    this.triggerUpdateDialog.emit();
  }

  private generateDateString(date: Date, user: User): string {
    if (!date || !user) return '-';

    const formattedDate = moment.utc(date).local().format('DD.MM.YYYY HH:mm');

    return `${transformNameToDisplayName(user)} (${formattedDate})`;
  }

  protected readonly GuidelineOrigin = GuidelineOrigin;
  protected readonly getOrganisationImage = getOrganisationImage;
}
